import { useEffect, useMemo, type RefObject } from 'react';

/**
 * Hook to handle wheel events for scrolling within an element when it is focused.
 * Prevents wheel events from bubbling to parent handlers (like React Flow) when scrolling
 * within the element bounds.
 *
 * @param scrollContainerRef - Ref to the scrollable container element
 * @param isFocused - Whether the element is currently focused/selected
 */
export const useCaptureElementWheelScroll = (scrollContainerRef: RefObject<HTMLDivElement | null>) => {
  const container = scrollContainerRef.current;

  const hasVerticalScroll = useMemo(
    () => container && container?.scrollHeight > container?.clientHeight,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [container?.scrollHeight, container?.clientHeight],
  );

  useEffect(() => {
    if (!container) {
      return;
    }
    const handleWheel = (event: WheelEvent) => {
      // Ignore pinch gestures (zoom in/out) on Mac - they use ctrlKey modifier
      if (event.ctrlKey) {
        return;
      }

      // Only handle vertical scroll events, ignore horizontal scroll (zoom on Mac)
      // On Mac, horizontal scroll gestures typically have significant deltaX and minimal deltaY
      // We want to capture events where deltaY is meaningful and deltaX is small or zero
      const absDeltaX = Math.abs(event.deltaX);
      const absDeltaY = Math.abs(event.deltaY);

      // Ignore if there's no vertical movement or if deltaX is greater than deltaY
      if (absDeltaY === 0 || absDeltaX > absDeltaY) {
        return;
      }

      // Check if the wheel event is within the container bounds
      const containerRect = container.getBoundingClientRect();
      const isWithinBounds =
        event.clientX >= containerRect.left &&
        event.clientX <= containerRect.right &&
        event.clientY >= containerRect.top &&
        event.clientY <= containerRect.bottom;

      if (isWithinBounds) {
        // Only stop propagation if there's actual vertical scroll on the container
        if (hasVerticalScroll) {
          event.stopPropagation();
        }
      }
    };

    // Listen at the capture phase to intercept before React Flow handlers
    container.addEventListener('wheel', handleWheel, true);

    return () => {
      container.removeEventListener('wheel', handleWheel, true);
    };
  }, [container, hasVerticalScroll]);
};
